import React, { CSSProperties, ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DeleteClassificationTypesDialog from '../../components/dialogs/DeleteClassificationTypesDialog/DeleteClassificationTypesDialog';
import NewClassificationTypeDialog from '../../components/dialogs/NewClassificationTypeDialog/NewClassificationTypeDialog';
import DataTableNew from '../../components/main/DataTableNew/DataTableNew';
import ShowOnRole from '../../components/main/ShowOnRole/ShowOnRole';
import { ClassificationTypeSortInput, Tag, UserRoleEnum, useGetClassificationTypeListQuery } from '../../generated/graphql';
import { Plus, Trash2 } from 'lucide-react';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import ClassificationDetailDialog from '../../components/dialogs/ClassificationDetailDialog/ClassificationDetailDialog';
import { gql, useApolloClient } from '@apollo/client';
import { createGraphQLOrder, TableSort } from '../../components/main/DataTableNew/DataTableSorting';

type ClassificationsProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

const Classifications = (props: ClassificationsProps) => {
    const { style } = props;
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [newClassificationTypeDialogVisible, setNewClassificationDialogVisible] = useState(false);
    const [deleteClassificationTypesDialogVisible, setDeleteClassificationsDialogVisible] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const classificationDetailId = searchParams.get('detail');
    const [typeCounts, setTypeCounts] = useState<Record<string, number>>({});
    const apolloClient = useApolloClient();
    const mountedRef = useRef(true);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const result = useGetClassificationTypeListQuery({
        variables: {
            contains: searchText,
            tagIds,
            order: createGraphQLOrder<ClassificationTypeSortInput>(sort),
        },
    });

    const fetchTypeCounts = useCallback(async () => {
        const nodes = result.data?.ClassificationTypeList?.nodes;
        if (!nodes) return;
        const QUERY = gql`
            query getClassificationCountForType($typeId: String) {
                ClassificationList(first: 10000, where: { typeId: { eq: $typeId } }) {
                    nodes { id }
                }
            }
        `;
        const counts: Record<string, number> = {};
        await Promise.all(
            nodes.filter(Boolean).map(async (node) => {
                const { data } = await apolloClient.query<{
                    ClassificationList?: { nodes?: Array<any> | null };
                }>({
                    query: QUERY,
                    variables: { typeId: node!.id },
                    fetchPolicy: 'cache-first',
                });
                counts[node!.id!] = data?.ClassificationList?.nodes?.length ?? 0;
            })
        );
        if (mountedRef.current) {
            setTypeCounts(counts);
        }
    }, [result.data, apolloClient]);

    useEffect(() => {
        mountedRef.current = true;
        fetchTypeCounts();
        return () => { mountedRef.current = false; };
    }, [fetchTypeCounts]);

    const handleOnClose = () => {
        setNewClassificationDialogVisible(false);
        setDeleteClassificationsDialogVisible(false);
        setSelectedIds([]);
        result.refetch();
    };

    const handleFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    return (
        <>
            <div className="page-container" style={style} data-testid="Classifications">
                {/* Header */}
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Classifications</h1>
                        <p className="page-subtitle">Manage classification types and their rules</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <RefreshButton onRefresh={() => result.refetch()} className="btn-ghost" iconSize={16} showLabel={false} />
                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                            {selectedIds.length > 0 && (
                                <button className="btn-danger" onClick={() => setDeleteClassificationsDialogVisible(true)}>
                                    <Trash2 size={16} />
                                    Delete ({selectedIds.length})
                                </button>
                            )}
                            <button className="btn-primary" onClick={() => setNewClassificationDialogVisible(true)}>
                                <Plus size={16} />
                                New Table
                            </button>
                        </ShowOnRole>
                    </div>
                </div>

                {/* Data Table */}
                <div className="card flex flex-col flex-1 min-h-0 overflow-hidden">
                    <DataTableNew
                        isLoading={result.loading}
                        data={result.data?.ClassificationTypeList?.nodes}
                        onRowClick={(row) => setSearchParams({ detail: row.id })}
                        showTags
                        showDescription
                        columns={[
                            {
                                id: 'count',
                                name: 'Count',
                                width: '80px',
                                render: (row: any) => (
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                        {row.id && typeCounts[row.id] != null ? typeCounts[row.id].toLocaleString() : '—'}
                                    </span>
                                ),
                            },
                        ]}
                        onSearchChanged={setSearchText}
                        onFilterChanged={handleFilterChanged}
                        onSelected={setSelectedIds}
                        sortingMode="server"
                        sort={sort}
                        onSortChange={setSort}
                        fetchMore={() => result.data?.ClassificationTypeList?.pageInfo?.hasNextPage && result.fetchMore({
                            variables: {
                                contains: searchText,
                                tagIds,
                                order: createGraphQLOrder<ClassificationTypeSortInput>(sort),
                                after: result.data?.ClassificationTypeList?.pageInfo.endCursor,
                            },
                            updateQuery: (previous, { fetchMoreResult }) => ({
                                ...previous,
                                ClassificationTypeList: fetchMoreResult?.ClassificationTypeList && previous.ClassificationTypeList ? {
                                    ...fetchMoreResult.ClassificationTypeList,
                                    nodes: [...(previous.ClassificationTypeList.nodes ?? []), ...(fetchMoreResult.ClassificationTypeList.nodes ?? [])],
                                } : previous.ClassificationTypeList,
                            }),
                        })}
                    />
                </div>
            </div>
            {newClassificationTypeDialogVisible && <NewClassificationTypeDialog visible={newClassificationTypeDialogVisible} onClose={handleOnClose} />}
            {deleteClassificationTypesDialogVisible && <DeleteClassificationTypesDialog ids={selectedIds} visible={deleteClassificationTypesDialogVisible} onClose={handleOnClose} />}
            {classificationDetailId && <ClassificationDetailDialog classificationTypeId={classificationDetailId} visible={!!classificationDetailId} onClose={() => { searchParams.delete('detail'); setSearchParams(searchParams); result.refetch().then(() => fetchTypeCounts()); }} />}
        </>
    );
};

export default Classifications;
